import { ReactNode, useCallback } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogProps,
    DialogTitle,
    IconButton,
    styled,
    Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton, LoadingButtonProps } from '@mui/lab';

export const SubmitButton = styled((props: LoadingButtonProps) => (
    <LoadingButton variant="contained" {...props} size="small" />
))(({ theme }) => ({
    textAlign: 'right',
    fontSize: 16,
    marginLeft: 'auto',
    '&:not(:first-of-type)': {
        marginLeft: theme.spacing(2),
    },
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
    padding: 0,
    position: 'absolute',
    right: 20,
    top: 20,
    color: theme.palette.grey[500],
}));

type BaseProps = Omit<DialogProps, 'title'> & {
    fullHeight?: boolean;
    title?: string | ReactNode;
    onClose?: () => void;
    loading?: boolean;
    disabled?: boolean | string;
    closable?: boolean;
    okLabel?: string;
    closeLabel?: string | null;
    onOk?: () => void;
    footer?: ReactNode;
};

export const BaseModal = (props: BaseProps) => {
    const { loading, okLabel, closeLabel, children, title, closable = true, ...rest } = props;
    const isDisabled = Boolean(props.disabled);
    const disabledReason = typeof props.disabled === 'string' ? props.disabled : undefined;

    const onClose = useCallback(
        (reason: string) => {
            if ((reason === 'backdropClick' || reason === 'escapeKeyDown') && !closable) return;
            props.onClose?.();
        },
        [closable, props.onClose],
    );

    const onOk = () => {
        props.onOk?.();
        onClose('okClick');
    };

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            {...rest}
            onClose={onClose}
            open={Boolean(props.open)}
            slotProps={{ paper: { sx: { height: props.fullHeight ? '100vh' : 'auto' } } }}
        >
            <DialogTitle variant="h6" sx={{ position: 'relative', pb: 1 }}>
                {title}
                {closable && (
                    <CloseButton
                        disabled={props.loading}
                        aria-label="close"
                        tabIndex={-1}
                        onClick={() => onClose('buttonClick')}
                    >
                        <CloseIcon />
                    </CloseButton>
                )}
            </DialogTitle>
            <DialogContent sx={{ pb: 0, '&.MuiDialogContent-root': { pt: 2 } }}>{children}</DialogContent>
            <DialogActions sx={{ p: 3, pt: 2, pb: 2, justifyContent: 'space-between' }}>
                {props.footer && <Box flex="1 0 0">{props.footer}</Box>}
                {closable && closeLabel !== null && (
                    <Button disabled={loading} onClick={props.onClose}>
                        {closeLabel || 'Cancel'}
                    </Button>
                )}
                {onOk && (
                    <Tooltip title={disabledReason}>
                        <span>
                            <SubmitButton loading={loading} disabled={isDisabled} onClick={onOk} sx={{ ml: 2 }}>
                                {okLabel || 'OK'}
                            </SubmitButton>
                        </span>
                    </Tooltip>
                )}
            </DialogActions>
        </Dialog>
    );
};

BaseModal.displayName = 'BaseModal';
